import { Component, EventEmitter, Input, Output } from '@angular/core';
import { HoverIdComponent } from '@clinic-platform/ui-components';

import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { StatusBadgeComponent } from '../../../shared/ui/status-badge/status-badge.component';
import { StatusBadgeViewModel } from '../../../shared/ui/status-badge/status-badge.models';
import { SlotItem } from '../data-access/slots.models';

@Component({
  selector: 'app-slots-table',
  standalone: true,
  imports: [HoverIdComponent, StatusBadgeComponent, TranslatePipe],
  template: `
    <div class="slots-table">
      <div class="slots-table__desktop">
        <table class="slots-table__table">
          <thead>
            <tr>
              <th>{{ 'Clinic' | t }}</th>
              <th>{{ 'Device' | t }}</th>
              <th>{{ 'Specialist' | t }}</th>
              <th>{{ 'Time' | t }}</th>
              <th>{{ 'common.status' | t }}</th>
              <th>{{ 'Reason' | t }}</th>
              @if (canCreateAppointment) {
                <th>{{ 'common.actions' | t }}</th>
              }
            </tr>
          </thead>

          <tbody>
            @for (item of items; track item.id) {
              <tr>
                <td>
                  <lib-hover-id [value]="item.clinicId">
                    @if (canOpenClinic && item.clinicId.trim()) {
                      <button
                        type="button"
                        class="slots-table__link-button"
                        (click)="clinicOpenClicked.emit(item.clinicId)"
                        [title]="clinicLabel(item)"
                      >
                        {{ clinicLabel(item) }}
                      </button>
                    } @else {
                      <span [title]="clinicLabel(item)">{{ clinicLabel(item) }}</span>
                    }
                  </lib-hover-id>
                </td>
                <td>
                  <lib-hover-id [value]="item.deviceId">
                    @if (canOpenDevice && item.deviceId?.trim()) {
                      <button
                        type="button"
                        class="slots-table__link-button"
                        (click)="deviceOpenClicked.emit(item.deviceId ?? '')"
                        [title]="deviceLabel(item)"
                      >
                        {{ deviceLabel(item) }}
                      </button>
                    } @else {
                      <span [title]="deviceLabel(item)">{{ deviceLabel(item) }}</span>
                    }
                  </lib-hover-id>
                </td>
                <td>
                  @if (item.specialistUserId) {
                    <lib-hover-id [value]="item.specialistUserId">
                      @if (canOpenSpecialist) {
                        <button
                          type="button"
                          class="slots-table__link-button"
                          (click)="specialistOpenClicked.emit(item.specialistUserId)"
                          [title]="specialistLabel(item)"
                        >
                          {{ specialistLabel(item) }}
                        </button>
                      } @else {
                        <span [title]="specialistLabel(item)">{{ specialistLabel(item) }}</span>
                      }
                    </lib-hover-id>
                  } @else {
                    —
                  }
                </td>
                <td>{{ formatTimeRange(item) }}</td>
                <td>
                  <app-status-badge
                    [label]="getStatusViewModel(item).label"
                    [tone]="getStatusViewModel(item).tone"
                  />
                </td>
                <td>{{ item.availabilityReason ?? '—' }}</td>
                @if (canCreateAppointment) {
                  <td>
                    <button
                      type="button"
                      class="slots-table__action"
                      [disabled]="!canCreateFromSlot(item)"
                      (click)="createAppointmentClicked.emit(item)"
                    >
                      {{ 'action.createAppointment' | t }}
                    </button>
                  </td>
                }
              </tr>
            }
          </tbody>
        </table>
      </div>

      <div class="slots-table__cards">
        @for (item of items; track item.id) {
          <article class="slots-table__card">
            <div class="slots-table__card-head">
              <div class="slots-table__card-head-main">
                @if (canOpenClinic && item.clinicId.trim()) {
                  <lib-hover-id [value]="item.clinicId">
                    <button
                      type="button"
                      class="slots-table__link-button slots-table__link-button--card"
                      (click)="clinicOpenClicked.emit(item.clinicId)"
                      [title]="clinicLabel(item)"
                    >
                      {{ clinicLabel(item) }}
                    </button>
                  </lib-hover-id>
                } @else {
                  <lib-hover-id [value]="item.clinicId">
                    <div class="slots-table__card-title">{{ clinicLabel(item) }}</div>
                  </lib-hover-id>
                }
                @if (canOpenDevice && item.deviceId?.trim()) {
                  <lib-hover-id [value]="item.deviceId">
                    <button
                      type="button"
                      class="slots-table__link-button slots-table__link-button--card"
                      (click)="deviceOpenClicked.emit(item.deviceId ?? '')"
                      [title]="deviceLabel(item)"
                    >
                      {{ deviceLabel(item) }}
                    </button>
                  </lib-hover-id>
                } @else {
                  <lib-hover-id [value]="item.deviceId">
                    <div class="slots-table__card-subtitle">{{ deviceLabel(item) }}</div>
                  </lib-hover-id>
                }
              </div>
              <app-status-badge
                [label]="getStatusViewModel(item).label"
                [tone]="getStatusViewModel(item).tone"
              />
            </div>

            <div class="slots-table__card-grid">
              <div class="slots-table__card-row">
                <div class="slots-table__card-label">{{ 'Specialist' | t }}</div>
                <div class="slots-table__card-value">
                  @if (item.specialistUserId) {
                    @if (canOpenSpecialist) {
                      <lib-hover-id [value]="item.specialistUserId">
                        <button
                          type="button"
                          class="slots-table__link-button slots-table__link-button--card"
                          (click)="specialistOpenClicked.emit(item.specialistUserId)"
                          [title]="specialistLabel(item)"
                        >
                          {{ specialistLabel(item) }}
                        </button>
                      </lib-hover-id>
                    } @else {
                      <lib-hover-id [value]="item.specialistUserId">
                        {{ specialistLabel(item) }}
                      </lib-hover-id>
                    }
                  } @else {
                    —
                  }
                </div>
              </div>
              <div class="slots-table__card-row">
                <div class="slots-table__card-label">{{ 'Time' | t }}</div>
                <div class="slots-table__card-value">{{ formatTimeRange(item) }}</div>
              </div>
              <div class="slots-table__card-row slots-table__card-row--full">
                <div class="slots-table__card-label">{{ 'Reason' | t }}</div>
                <div class="slots-table__card-value">{{ item.availabilityReason ?? '—' }}</div>
              </div>
            </div>

            @if (canCreateAppointment) {
              <button
                type="button"
                class="slots-table__card-action"
                [disabled]="!canCreateFromSlot(item)"
                (click)="createAppointmentClicked.emit(item)"
              >
                {{ 'action.createAppointment' | t }}
              </button>
            }
          </article>
        }
      </div>
    </div>
  `,
  styles: [`
    .slots-table {
      background: #ffffff;
    }

    .slots-table__desktop {
      overflow-x: auto;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
    }

    .slots-table__table {
      width: 100%;
      border-collapse: collapse;
      min-width: 1120px;
    }

    .slots-table__table th,
    .slots-table__table td {
      text-align: left;
      padding: 12px 14px;
      border-bottom: 1px solid #f3f4f6;
      vertical-align: middle;
      font-size: 13px;
    }

    .slots-table__table th {
      background: #f9fafb;
      color: #374151;
    }

    .slots-table__table td {
      color: #111827;
    }

    .slots-table__action {
      height: 32px;
      border: 1px solid #d1d5db;
      background: #ffffff;
      border-radius: 8px;
      padding: 0 10px;
      cursor: pointer;
      font-size: 12px;
      white-space: nowrap;
    }

    .slots-table__link-button {
      border: none;
      padding: 0;
      background: transparent;
      color: #2563eb;
      font: inherit;
      text-align: left;
      cursor: pointer;
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
    }

    .slots-table__link-button--card {
      font-size: 13px;
      font-weight: 600;
    }

    .slots-table__action:disabled {
      opacity: 0.5;
      cursor: default;
    }

    .slots-table__cards {
      display: none;
      gap: 14px;
    }

    .slots-table__card {
      border: 1px solid #e5e7eb;
      border-radius: 18px;
      background: linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.95));
      box-shadow: 0 12px 32px rgba(15, 23, 42, 0.06);
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 14px;
    }

    .slots-table__card-head {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 12px;
    }

    .slots-table__card-head-main {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .slots-table__card-title {
      font-size: 16px;
      font-weight: 700;
      color: #111827;
      overflow-wrap: anywhere;
    }

    .slots-table__card-subtitle {
      font-size: 13px;
      color: #6b7280;
      overflow-wrap: anywhere;
    }

    .slots-table__card-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 14px 16px;
    }

    .slots-table__card-row {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .slots-table__card-row--full {
      grid-column: 1 / -1;
    }

    .slots-table__card-label {
      font-size: 11px;
      line-height: 1.2;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #6b7280;
    }

    .slots-table__card-value {
      font-size: 14px;
      line-height: 1.45;
      color: #111827;
      overflow-wrap: anywhere;
    }

    .slots-table__card-value--mono {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
      font-size: 12px;
    }

    .slots-table__card-action {
      width: 100%;
      min-height: 40px;
      border: none;
      border-radius: 12px;
      background: linear-gradient(135deg, #2563eb, #1d4ed8);
      color: #ffffff;
      padding: 10px 14px;
      cursor: pointer;
      font-size: 13px;
      font-weight: 600;
    }

    .slots-table__card-action:disabled {
      opacity: 0.5;
      cursor: default;
    }

    @media (max-width: 900px) {
      .slots-table__desktop {
        display: none;
      }

      .slots-table__cards {
        display: grid;
      }
    }

    @media (max-width: 640px) {
      .slots-table__card {
        border-radius: 16px;
        padding: 14px;
      }

      .slots-table__card-grid {
        grid-template-columns: minmax(0, 1fr);
      }
    }
  `]
})
export class SlotsTableComponent
{
  @Input()
  public items: SlotItem[] = [];

  @Input()
  public clinics: Array<{ id: string; label: string }> = [];

  @Input()
  public devices: Array<{ id: string; label: string }> = [];

  @Input()
  public specialists: Array<{ id: string; label: string }> = [];

  @Input()
  public canCreateAppointment = true;

  @Input()
  public clinicTimeZones: Record<string, string> = {};

  @Input()
  public canOpenDevice = false;

  @Input()
  public canOpenClinic = false;

  @Input()
  public canOpenSpecialist = false;

  @Output()
  public readonly createAppointmentClicked = new EventEmitter<SlotItem>();

  @Output()
  public readonly deviceOpenClicked = new EventEmitter<string>();

  @Output()
  public readonly clinicOpenClicked = new EventEmitter<string>();

  @Output()
  public readonly specialistOpenClicked = new EventEmitter<string>();

  protected getStatusViewModel(item: SlotItem): StatusBadgeViewModel
  {
    if (this.isSlotInPast(item))
    {
      return { label: 'status.past', tone: 'neutral' };
    }

    switch (item.status)
    {
      case 'available':
        return { label: 'status.available', tone: 'success' };

      case 'reserved':
        return { label: 'status.reserved', tone: 'warning' };

      case 'blocked':
        return { label: 'status.blocked', tone: 'danger' };

      case 'completed':
        return { label: 'status.completed', tone: 'info' };

      default:
        return { label: item.status, tone: 'neutral' };
    }
  }

  protected canCreateFromSlot(slot: SlotItem): boolean
  {
    if (slot.status !== 'available')
    {
      return false;
    }

    return new Date(slot.startTime).getTime() > Date.now();
  }

  private isSlotInPast(slot: SlotItem): boolean
  {
    const slotStart = new Date(slot.startTime).getTime();
    if (Number.isNaN(slotStart))
    {
      return false;
    }

    return slotStart < Date.now();
  }

  protected clinicLabel(item: SlotItem): string
  {
    const label = item.clinicLabel?.trim() ?? '';
    if (label) {
      return label;
    }

    const clinicId = item.clinicId;
    return this.clinics.find((item) => item.id === clinicId)?.label ?? 'Clinic';
  }

  protected deviceLabel(item: SlotItem): string
  {
    const label = item.deviceLabel?.trim() ?? '';
    if (label) {
      return label;
    }

    const deviceId = item.deviceId;
    if (!deviceId) {
      return 'No device';
    }

    return this.devices.find((item) => item.id === deviceId)?.label ?? 'Device';
  }

  protected specialistLabel(item: SlotItem): string
  {
    const label = item.specialistLabel?.trim() ?? '';
    if (label) {
      return label;
    }

    const userId = item.specialistUserId ?? '';
    return this.specialists.find((item) => item.id === userId)?.label ?? 'Specialist';
  }

  protected formatDateTime(item: SlotItem, value: string): string
  {
    const raw = value?.trim() ?? '';
    if (!raw) {
      return '—';
    }

    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) {
      return raw;
    }

    const timeZone = item.clinicTimeZone?.trim() || this.clinicTimeZones[item.clinicId]?.trim() || undefined;

    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      timeZone,
    }).format(date);
  }

  protected formatTimeRange(item: SlotItem): string
  {
    return `${this.formatDateTime(item, item.startTime)} - ${this.formatDateTime(item, item.endTime)}`;
  }

}
